import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dpsTheme } from '../../../styles/dpsTheme';
import { useDeviceSize } from '../../../hooks/useMediaQuery';
import { useWhiteLabel } from '../../../hooks/useWhiteLabel';

interface DPSHeaderProps {
  onDownloadPDF?: () => void;
  onMenuToggle?: () => void;
}

export const DPSHeader: React.FC<DPSHeaderProps> = ({ onDownloadPDF, onMenuToggle }) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceSize();
  const { appName, logoUrl } = useWhiteLabel();

  const SHOW_PDF_BUTTON = true;

  return (
    <header
      style={{
        background: dpsTheme.colors.background.header,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${dpsTheme.colors.border.primary}`,
        padding: isMobile ? '12px 16px' : '16px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', cursor: 'pointer' }} onClick={() => navigate('/')}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={appName}
            style={{ height: isMobile ? '32px' : '36px', width: 'auto', objectFit: 'contain' }}
          />
        ) : (
          <div
            style={{
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              background: dpsTheme.colors.accent.blue.gradient,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width={isMobile ? '18' : '20'} height={isMobile ? '18' : '20'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
        )}
        {!isMobile && <span style={{ fontSize: '18px', fontWeight: 600, color: dpsTheme.colors.text.primary }}>{appName}</span>}
      </div>
      <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', alignItems: 'center' }}>
        {isMobile && onMenuToggle && (
          <button
            onClick={onMenuToggle}
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: `1px solid ${dpsTheme.colors.border.primary}`,
              background: 'rgba(255, 255, 255, 0.08)',
              color: dpsTheme.colors.text.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '44px',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        {SHOW_PDF_BUTTON && onDownloadPDF && !isMobile && (
          <button
            onClick={onDownloadPDF}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: `1px solid ${dpsTheme.colors.border.primary}`,
              background: 'rgba(255, 255, 255, 0.05)',
              color: dpsTheme.colors.text.secondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {!isMobile && 'Télécharger PDF'}
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: isMobile ? '8px 12px' : '10px 20px',
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: isMobile ? '12px' : '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: 'none',
            background: dpsTheme.colors.accent.blue.gradient,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <svg width={isMobile ? '14' : '16'} height={isMobile ? '14' : '16'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {!isMobile && 'Mon compte'}
        </button>
      </div>
    </header>
  );
};